/**
 * Feature switch for the `api/SurveyBuilder` write API.
 *
 * On by default. `Survey/EditSurvey` stays as the fallback - both as the way back if this API
 * misbehaves, and as the path a save takes automatically when it carries an edit the new API has
 * no route for (see `saveSurveyEdits`).
 *
 * Two sources, most specific first, and the first one with an opinion wins:
 *
 *   1. `window.__KAP_CONFIG__.USE_SURVEY_BUILDER_API` - set by `public/runtime-config.js`, which is
 *      served as a plain file rather than bundled, so editing it on the server and reloading rolls
 *      the flag back for everyone with no rebuild and no deploy. It ships as `null`, meaning "no
 *      opinion", so it never silently overrides the build-time default until somebody sets it.
 *   2. `process.env.REACT_APP_USE_SURVEY_BUILDER_API` - the build-time default, `true` everywhere.
 *
 * Only "true" and "false" count as an opinion. There is deliberately no per-browser override: a
 * value stored in one person's browser would outrank the served config, which is the one lever
 * that has to reach everybody at once for a rollback to mean anything.
 */

export const SURVEY_BUILDER_API_FLAG = "USE_SURVEY_BUILDER_API";

/** "true"/"false" as a boolean; anything else - unset, null, junk - as `null` for "no opinion". */
function readTriState(value) {
   if (value === true || value === false) {
      return value;
   }

   const normalised = String(value).toLowerCase();

   if (normalised === "true") return true;
   if (normalised === "false") return false;

   return null;
}

function readRuntimeConfig() {
   try {
      return readTriState(window.__KAP_CONFIG__?.[SURVEY_BUILDER_API_FLAG]);
   } catch (_error) {
      // A page with no `window` at all must not be what decides how a survey is saved.
      return null;
   }
}

/** True when the builder should write through `api/SurveyBuilder` instead of `Survey/EditSurvey`. */
export function isSurveyBuilderApiEnabled() {
   const runtime = readRuntimeConfig();

   if (runtime !== null) {
      return runtime;
   }

   return readTriState(process.env.REACT_APP_USE_SURVEY_BUILDER_API) === true;
}
